'use client';

import Button from '@components/button';
import { MobileMenuToggle } from '@components/header/components/mobile-menu-toggle.view';
import NavigationItem from '@components/header/components/navigation/navigation-item.view';
import classNames from 'classnames';
import Link from 'next/link';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { NavigationLink } from 'types/navigation-link';
import useDelayedRender from 'utils/hooks/use-delayed-render';
import { validateString } from 'utils/validate-string';

import styles from './mobile-navigation-list.module.scss';

interface Props {
  readonly links: NavigationLink[];
}

const listClass = validateString(styles.list);
const renderedListClass = validateString(styles.renderedList);
const listItemClass = validateString(styles.listItem);
const mobileToggleClass = validateString(styles.mobileToggle);

const ANIMATION_DELAY_START = 150;
const ANIMATION_DELAY_INCREMENT = 75;

function MobileNavigation({ links }: Props): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { mounted: isMenuMounted, rendered: isMenuRendered } = useDelayedRender(
    isMenuOpen,
    {
      enterDelay: 20,
      exitDelay: 300,
    },
  );

  const toggleMenu = useCallback(() => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
      document.body.style.overflow = '';
    } else {
      setIsMenuOpen(true);
      document.body.style.overflow = 'hidden';
    }
  }, [isMenuOpen]);

  useEffect(() => {
    return function cleanup() {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      <MobileMenuToggle
        className={mobileToggleClass}
        onToggle={toggleMenu}
        toggled={isMenuOpen}
      />
      {isMenuMounted === true && (
        <ul
          className={classNames(
            listClass,
            isMenuRendered === true && renderedListClass,
          )}
        >
          {/* Desktop Menu */}
          {links
            .filter(link =>
              typeof link.highlight !== 'undefined'
                ? link.highlight === false
                : true,
            )
            .map((link, idx) => (
              <div
                className={listItemClass}
                key={link.label}
                style={{
                  transitionDelay: `${
                    ANIMATION_DELAY_START + idx * ANIMATION_DELAY_INCREMENT
                  }ms`,
                }}
              >
                <NavigationItem link={link} onClick={toggleMenu} />
              </div>
            ))}
          {/* Highlights */}
          {links
            .filter(link =>
              typeof link.highlight !== 'undefined'
                ? link.highlight === true
                : false,
            )
            .map(link => (
              <li key={link.label} style={{ marginTop: '2rem' }}>
                <Link href={link.url}>
                  <Button color="primary" variant="solid">
                    {link.label}
                  </Button>
                </Link>
              </li>
            ))}

          {/* <div
            className={classNames(listItemClass, darkToggleClass)}
            style={{
              transitionDelay: `${DARK_MODE_DELAY}ms`,
            }}
          >
            <ThemeToggler />
          </div> */}
        </ul>
      )}
    </>
  );
}

export default MobileNavigation;
